<template>
  <div class="tk">
    <div class="card tk-card">
      <div class="tk-head">
        <button type="button" class="tk-icon-btn" aria-label="رجوع" @click="back"><i class="fa fa-arrow-right"></i></button>
        <span class="tk-head-icon" :class="ticket.urgent ? 'tk-head-icon-urgent' : ''"><i class="fa" :class="headIcon"></i></span>
        <div class="tk-head-text">
          <strong class="tk-title">{{ ticket.title || 'تذكرة دعم' }}</strong>
          <small>
            <span :class="{ 'tk-red': ticket.urgent }">{{ typeLabel }}</span>
            <template v-if="loaded"> · <span :class="ticket.status == 1 ? 'tk-muted' : 'tk-green'">{{ ticket.status == 1 ? 'مغلقة' : 'مفتوحة' }}</span></template>
          </small>
        </div>
        <button v-if="canClose" type="button" class="tk-close-btn" @click="confirmClose = true">
          <i class="fa fa-check-circle"></i> إغلاق التذكرة
        </button>
      </div>

      <div v-if="confirmClose" class="tk-confirm" role="alertdialog" aria-label="إغلاق التذكرة؟">
        <div>
          <strong>إغلاق التذكرة؟</strong>
          <div>أغلقها إذا حُلّت المشكلة. يمكنك فتح تذكرة جديدة في أي وقت.</div>
        </div>
        <div class="tk-confirm-actions">
          <button type="button" class="tk-btn tk-btn-dark" :disabled="closing" @click="close">{{ closing ? 'جارٍ الإغلاق…' : 'إغلاق' }}</button>
          <button type="button" class="tk-btn" @click="confirmClose = false">تراجع</button>
        </div>
      </div>

      <div v-if="justCreated && ticket.status != 1" class="tk-notice">
        <i class="fa fa-check-circle"></i> وصلت التذكرة لفريق الدعم، سيصلك الرد هنا وإشعاراً على الجوال
      </div>

      <div ref="body" class="tk-body" @scroll="onScroll">
        <div v-if="!loaded && !error" class="tk-state"><i class="fa fa-spinner fa-spin"></i></div>
        <div v-if="error" class="tk-state tk-state-error">
          <div>{{ error }}</div>
          <button type="button" class="tk-btn" @click="load(false)">إعادة المحاولة</button>
        </div>
        <template v-for="(m, i) in messages">
          <div v-if="newDay(i)" :key="'d' + m._id" class="tk-day"><span>{{ dayOf(m.at || m.date) }}</span></div>
          <div :key="m._id" class="tk-row" :class="m.from == 1 ? 'tk-row-support' : 'tk-row-school'">
            <div class="tk-bubble" :class="m.from == 1 ? 'tk-bubble-support' : 'tk-bubble-school'">
              <div v-if="m.from == 1" class="tk-by"><i class="fa fa-headphones"></i> الدعم الفني{{ m.by_name ? ' · ' + m.by_name : '' }}</div>
              <div v-else-if="m.by_name" class="tk-by">{{ m.by_name }}</div>
              <a v-if="m.file && m.kind == 'image'" :href="m.file" target="_blank" rel="noopener" class="tk-img-link">
                <img :src="m.file" alt="صورة مرفقة" class="tk-img">
              </a>
              <template v-else-if="m.file && m.kind == 'voice'">
                <div class="tk-voice-label"><i class="fa fa-microphone"></i> رسالة صوتية<template v-if="m.duration"> · <bdi dir="ltr">{{ seconds(m.duration) }}</bdi></template></div>
                <audio :src="m.file" controls preload="none" class="tk-audio"></audio>
              </template>
              <a v-else-if="m.file" :href="m.file" target="_blank" rel="noopener" class="tk-file">
                <i class="fa fa-file-o"></i>
                <span>{{ m.file_name || 'عرض المرفق' }}</span>
              </a>
              <div v-if="m.message" class="tk-text">{{ m.message }}</div>
              <div class="tk-meta">
                <span>{{ timeOf(m.at || m.date) }}</span>
                <i v-if="m.from == 0" class="fa" :class="m.seen ? 'fa-check-circle' : 'fa-check'" :title="m.seen ? 'تمت رؤيتها' : 'تم الإرسال'"></i>
              </div>
            </div>
          </div>
        </template>
      </div>

      <div v-if="loaded && ticket.status == 1" class="tk-closed">
        <span><i class="fa fa-lock"></i> التذكرة مغلقة، لا يمكن إضافة رسائل لها.</span>
        <button type="button" class="tk-btn tk-btn-red" @click="newTicket">افتح تذكرة جديدة</button>
      </div>
      <div v-else-if="loaded" class="tk-compose">
        <div v-if="sendError" class="tk-send-error">{{ sendError }}</div>
        <div v-if="uploading" class="tk-progress">جارٍ الرفع… {{ progress }}%</div>
        <div class="tk-compose-row">
          <button type="button" class="tk-icon-btn tk-attach" :disabled="busy || recording" aria-label="إرفاق صورة أو ملف" title="إرفاق صورة أو ملف" @click="pickFile">
            <i class="fa fa-paperclip"></i>
          </button>
          <input ref="file" type="file" class="tk-hidden" :accept="accept" @change="filePicked">
          <textarea v-show="!recording" v-model="text" class="tk-input" rows="1" maxlength="4000" placeholder="اكتب رسالتك…"
                    aria-label="اكتب رسالتك" :disabled="busy" @keydown="key"></textarea>
          <button v-if="text.trim()" type="button" class="tk-send" :disabled="busy" aria-label="إرسال" @click="sendText">
            <i class="fa fa-paper-plane" style="transform: scaleX(-1)"></i>
          </button>
          <voice-recorder v-else small :disabled="busy" @recorded="voiceRecorded" @recording="recording = $event" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import VoiceRecorder from '@/views/support/VoiceRecorder.vue';
import { call, upload, kindOf, timeOf, dayOf, seconds, FILE_ACCEPT } from '@/views/support/support-api';

// One ticket of the school (urgent «الدعم الفني السريع» tickets, older problem reports and suggestions):
// the conversation with the support team, with images, files and voice notes. Refreshes every 10 s.
// The WhatsApp message of the first support reply links here (s.tahdir.net/ticket/<id>).
export default {
  components: { VoiceRecorder },
  data(){
    return {
      ticket: {}, messages: [], loaded: false, error: '', text: '', busy: false, uploading: false, progress: 0,
      sendError: '', recording: false, confirmClose: false, closing: false, timer: null, atBottom: true,
      accept: FILE_ACCEPT,
    };
  },
  computed: {
    id(){ return (this.$route.params.id || '').toString(); },
    justCreated(){ return this.$route.query.new == '1'; },
    typeLabel(){
      if (this.ticket.urgent) return 'الدعم الفني السريع';
      return this.ticket.type == 'feedback' ? 'رأي / اقتراح' : 'تذكرة دعم';
    },
    headIcon(){
      if (this.ticket.urgent) return 'fa-bell';
      return this.ticket.type == 'feedback' ? 'fa-lightbulb-o' : 'fa-wrench';
    },
    canClose(){ return this.loaded && this.ticket.status == 0 && this.ticket.type != 'feedback'; },
  },
  watch: {
    id(){ this.reset(); this.load(false); },
  },
  created(){
    this.load(false);
    var g = this;
    g.timer = setInterval(function(){
      if (document.visibilityState == 'visible' && !g.busy && g.loaded) g.load(true);
    }, 10000);
  },
  beforeDestroy(){
    clearInterval(this.timer);
  },
  methods: {
    timeOf: timeOf,
    dayOf: dayOf,
    seconds: seconds,
    reset(){
      this.ticket = {};
      this.messages = [];
      this.loaded = false;
      this.error = '';
      this.confirmClose = false;
    },
    back(){ this.$router.push('/feedback'); },
    newTicket(){ this.$router.push('/feedback?open=urgent'); },
    newDay(i){
      if (i == 0) return true;
      var a = this.messages[i - 1], b = this.messages[i];
      return dayOf(a.at || a.date) != dayOf(b.at || b.date);
    },
    load(silent){
      var g = this;
      var count = g.messages.length;
      call('/ticket', { id: g.id }).then(function(r){
        if (!r.ok) {
          if (!silent || !g.loaded) g.error = r.message || 'تعذّر تحميل التذكرة';
          return;
        }
        g.error = '';
        g.ticket = r.data.ticket || {};
        g.messages = r.data.messages || [];
        var first = !g.loaded;
        g.loaded = true;
        if (first || g.atBottom || g.messages.length > count) g.scroll();
      });
    },
    onScroll(){
      var b = this.$refs.body;
      if (b) this.atBottom = b.scrollHeight - b.scrollTop - b.clientHeight < 60;
    },
    scroll(){
      var g = this;
      g.$nextTick(function(){ var b = g.$refs.body; if (b) { b.scrollTop = b.scrollHeight; g.atBottom = true; } });
    },
    key(e){
      if (e.key == 'Enter' && !e.shiftKey && !e.isComposing) { e.preventDefault(); this.sendText(); }
    },
    send(payload){
      var g = this;
      g.busy = true;
      g.sendError = '';
      return call('/ticket/send', Object.assign({ id: g.id }, payload)).then(function(r){
        g.busy = false;
        if (r.ok) {
          if (r.data && r.data.message) g.messages.push(r.data.message);
          g.scroll();
          return true;
        }
        g.sendError = r.message;
        if (/مغلقة/.test(r.message || '')) g.load(true);
        return false;
      });
    },
    sendText(){
      var t = this.text.trim();
      if (!t || this.busy) return;
      var g = this;
      g.send({ message: t, kind: 'text' }).then(function(ok){ if (ok) g.text = ''; });
    },
    pickFile(){ this.$refs.file.click(); },
    filePicked(e){
      var f = (e.target.files || [])[0];
      e.target.value = '';
      if (!f) return;
      if (f.size > 15 * 1024 * 1024) { this.sendError = 'حجم الملف أكبر من 15 ميجابايت'; return; }
      this.uploadAndSend(f, f.name, 0);
    },
    voiceRecorded(v){
      this.uploadAndSend(v.blob, v.name, v.duration);
    },
    uploadAndSend(file, name, duration){
      var g = this;
      g.busy = true;
      g.uploading = true;
      g.progress = 0;
      g.sendError = '';
      upload(file, name, function(p){ g.progress = p; }).then(function(r){
        g.uploading = false;
        if (!r.ok) {
          g.busy = false;
          g.sendError = r.message;
          return;
        }
        g.send({ message: '', file: r.data.url, kind: r.data.kind || kindOf(name), file_name: r.data.file_name || name, duration: duration || 0 });
      });
    },
    close(){
      var g = this;
      g.closing = true;
      call('/ticket/close', { id: g.id }).then(function(r){
        g.closing = false;
        g.confirmClose = false;
        if (!r.ok) { g.sendError = r.message; return; }
        g.load(true);
      });
    },
  },
};
</script>

<style scoped>
.tk { max-width: 860px; margin: 0 auto; }
.tk-card { margin: 0; border-radius: 16px; overflow: hidden; display: flex; flex-direction: column; }
.tk-head { display: flex; align-items: center; gap: 12px; padding: 14px 16px; border-bottom: 1px solid rgba(127, 127, 127, 0.15); }
.tk-icon-btn {
  width: 40px; height: 40px; flex: 0 0 auto; border-radius: 12px; border: 1px solid rgba(127, 127, 127, 0.22);
  background: transparent; color: inherit; cursor: pointer; display: inline-flex; align-items: center; justify-content: center;
}
.tk-icon-btn:hover:not(:disabled) { background: rgba(127, 127, 127, 0.08); }
.tk-icon-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.tk-head-icon { width: 44px; height: 44px; flex: 0 0 auto; border-radius: 13px; background: rgba(37, 99, 235, 0.1); color: var(--primary); display: flex; align-items: center; justify-content: center; font-size: 19px; }
.tk-head-icon-urgent { background: rgba(234, 84, 85, 0.12); color: #ea5455; }
.tk-head-text { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 2px; }
.tk-title { font-size: 15px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.tk-red { color: #c62828; font-weight: 700; }
.tk-green { color: #1f9d55; font-weight: 700; }
.tk-muted { opacity: 0.7; font-weight: 700; }
.tk-close-btn { border: 1px solid rgba(127, 127, 127, 0.25); background: transparent; color: inherit; border-radius: 10px; padding: 7px 12px; cursor: pointer; font-weight: 600; white-space: nowrap; }
.tk-close-btn:hover { background: rgba(127, 127, 127, 0.08); }

.tk-confirm { display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap; padding: 12px 16px; background: rgba(255, 159, 67, 0.12); line-height: 1.8; }
.tk-confirm-actions { display: flex; gap: 8px; }
.tk-notice { padding: 10px 16px; background: rgba(40, 199, 111, 0.12); color: #1f9d55; font-weight: 600; }

.tk-body { height: calc(100vh - 340px); min-height: 340px; overflow-y: auto; padding: 16px; display: flex; flex-direction: column; gap: 10px; background: rgba(127, 127, 127, 0.04); }
.tk-state { margin: auto; text-align: center; opacity: 0.7; display: flex; flex-direction: column; gap: 10px; align-items: center; font-size: 15px; }
.tk-state-error { color: #c62828; opacity: 1; }
.tk-day { display: flex; justify-content: center; }
.tk-day span { font-size: 12px; padding: 3px 12px; border-radius: 999px; background: rgba(127, 127, 127, 0.14); }
.tk-row { display: flex; }
.tk-row-school { justify-content: flex-start; }
.tk-row-support { justify-content: flex-end; }
.tk-bubble { max-width: 78%; padding: 10px 14px; border-radius: 16px; display: flex; flex-direction: column; gap: 6px; overflow-wrap: anywhere; }
.tk-bubble-school { background: var(--primary); color: #fff; }
.tk-bubble-support { background: var(--tk-surface, #fff); border: 1px solid rgba(127, 127, 127, 0.2); }
.tk-by { font-size: 12px; font-weight: 700; opacity: 0.9; }
.tk-bubble-support .tk-by { color: var(--primary); }
.tk-text { white-space: pre-wrap; line-height: 1.8; }
.tk-img-link { display: block; }
.tk-img { display: block; max-width: 260px; max-height: 260px; width: auto; border-radius: 10px; object-fit: cover; }
.tk-voice-label { font-size: 12px; font-weight: 700; opacity: 0.9; }
.tk-audio { width: 260px; max-width: 100%; height: 38px; }
.tk-file { display: inline-flex; align-items: center; gap: 8px; padding: 8px 12px; border-radius: 10px; background: rgba(127, 127, 127, 0.14); color: inherit; text-decoration: none; font-weight: 600; }
.tk-bubble-school .tk-file { background: rgba(255, 255, 255, 0.18); color: #fff; }
.tk-file:hover { text-decoration: underline; color: inherit; }
.tk-meta { display: flex; align-items: center; gap: 6px; font-size: 11px; opacity: 0.75; }

.tk-closed { display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap; padding: 14px 16px; border-top: 1px solid rgba(127, 127, 127, 0.15); }
.tk-compose { padding: 12px 16px; border-top: 1px solid rgba(127, 127, 127, 0.15); display: flex; flex-direction: column; gap: 8px; }
.tk-compose-row { display: flex; align-items: flex-end; gap: 8px; }
.tk-input {
  flex: 1; min-width: 0; resize: none; max-height: 140px; border-radius: 12px; padding: 10px 12px; line-height: 1.6;
  border: 1px solid rgba(127, 127, 127, 0.3); background: transparent; color: inherit; font: inherit;
}
.tk-input:focus { outline: 2px solid rgba(37, 99, 235, 0.3); border-color: var(--primary); }
.tk-send { width: 42px; height: 42px; flex: 0 0 auto; border: 0; border-radius: 12px; background: var(--primary); color: #fff; cursor: pointer; }
.tk-send:disabled { opacity: 0.5; }
.tk-hidden { display: none; }
.tk-send-error { padding: 8px 12px; border-radius: 10px; background: rgba(234, 84, 85, 0.1); color: #c62828; }
.tk-progress { font-size: 13px; opacity: 0.75; }
.tk-btn { border: 1px solid rgba(127, 127, 127, 0.3); background: transparent; color: inherit; border-radius: 10px; padding: 7px 14px; cursor: pointer; font-weight: 600; }
.tk-btn-dark { background: #4b4b4b; border-color: #4b4b4b; color: #fff; }
.tk-btn-red { background: #ea5455; border-color: #ea5455; color: #fff; }

@media (max-width: 575px) {
  .tk-body { height: calc(100vh - 300px); padding: 12px; }
  .tk-bubble { max-width: 88%; }
  .tk-img { max-width: 200px; }
  .tk-close-btn { padding: 7px 9px; font-size: 12px; }
}
</style>
<style>
.dark-layout .tk { --tk-surface: #283046; }
</style>
